<script lang="ts">
  import { addCredits } from '../../lib/components/map/credits';
  import { onDestroy, onMount } from 'svelte';
  import { Link, navigate } from 'svelte-routing';
  import * as maplibregl from 'maplibre-gl';
  import { api } from '../../lib/api/client';
  import type { MapInfo } from '../../lib/api/types';
  import { isAuthenticated } from '../../lib/stores/auth';
  import { followTheme, northUpMap, styleFor } from '../../lib/components/map/theme';
  import { sportOf } from '../../lib/utils/sports';
  import { decodePolyline, type Heatmap } from '../../lib/utils/training';
  import { t } from '../../lib/i18n/index.ts';

  // Heatmap is every route of the member's that Kehai keeps, drawn at once
  // (spec 006 FR-091, 006 FR-092): one line layer at low opacity in the
  // theme's accent, so roads taken often draw brighter, filtered by sport
  // and period and fitted to the routes.
  type Period = 'this' | 'last' | 'all';
  const PERIODS: { id: Period; name: string }[] = [
    { id: 'this', name: t('activitiesHeatmapThisYear') },
    { id: 'last', name: t('activitiesHeatmapLastYear') },
    { id: 'all', name: t('activitiesHeatmapAll') },
  ];

  $effect(() => {
    if (!$isAuthenticated) navigate('/login', { replace: true });
  });

  let el: HTMLDivElement | undefined = $state();
  let map: maplibregl.Map | null = null;
  let stopTheme: (() => void) | null = null;
  let ready = false;
  let sport = $state('');
  let period = $state<Period>('this');
  let sports = $state<string[]>([]);
  let heat = $state<Heatmap | null>(null);
  let loading = $state(false);
  let error = $state('');
  // seq drops an answer that a later filter has already replaced.
  let seq = 0;

  $effect(() => {
    const s = sport, p = period;
    const q = new URLSearchParams();
    if (s) q.set('sport', s);
    const y = new Date().getFullYear();
    if (p !== 'all') {
      const from = p === 'this' ? y : y - 1;
      q.set('from', new Date(from, 0, 1).toISOString());
      q.set('to', new Date(from + 1, 0, 1).toISOString());
    }
    const mine = ++seq;
    loading = true;
    api.get<Heatmap>(`/me/heatmap?${q}`)
      .then((got) => {
        if (mine !== seq) return;
        heat = got;
        if (got.sports.length) sports = got.sports;
        error = '';
        draw(true);
      })
      .catch((e) => { if (mine === seq) error = e.message; })
      .finally(() => { if (mine === seq) loading = false; });
  });

  // accent reads the theme's accent when drawing, since the theme can change.
  function accent(): string {
    return getComputedStyle(document.documentElement).getPropertyValue('--theme-accent').trim();
  }

  function draw(fit: boolean) {
    if (!map || !ready || !heat) return;
    const fc: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: heat.routes.map((r) => ({
        type: 'Feature',
        properties: { sport: r.sport },
        geometry: { type: 'MultiLineString', coordinates: r.lines.map(decodePolyline).filter((l) => l.length > 1) },
      })),
    };
    (map.getSource('heat') as maplibregl.GeoJSONSource).setData(fc);
    if (fit && heat.bounds) {
      const [w, s, e, n] = heat.bounds;
      map.fitBounds([[w, s], [e, n]], { padding: 40, maxZoom: 15, duration: 0 });
    }
  }

  onMount(async () => {
    let info: MapInfo | null = null;
    try {
      info = await api.get<MapInfo>('/map');
    } catch {
      // A blank background still shows the routes.
    }
    if (!el) return;
    map = northUpMap({ container: el, style: styleFor(info), center: [-98.5, 39.8], zoom: 3, attributionControl: false });
    addCredits(map, info, 'other');
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'bottom-right');
    stopTheme = followTheme(map, info, () => {
      map!.addSource('heat', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map!.addLayer({
        id: 'heat',
        type: 'line',
        source: 'heat',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': accent(), 'line-opacity': 0.25, 'line-width': ['interpolate', ['linear'], ['zoom'], 8, 1.5, 14, 3, 18, 6] },
      });
      const first = !ready;
      ready = true;
      draw(first);
    });
  });

  onDestroy(() => {
    stopTheme?.();
    map?.remove();
  });

  let empty = $derived(heat != null && heat.routes.length === 0);
</script>

<div class="flex-1 flex flex-col min-h-0">
  <div class="px-4 py-3 flex flex-wrap items-center gap-3">
    <Link to="/activities/progress" class="text-sm hover:opacity-80"><i aria-hidden="true" class="las la-arrow-left mr-1"></i>{t('activitiesProgress')}</Link>
    <h1 class="text-lg font-semibold mr-auto">{t('activitiesHeatmap')}</h1>
    <select class="input py-1 text-sm" bind:value={sport} aria-label={t('activitiesSport')}>
      <option value="">{t('activitiesAllSports')}</option>
      {#each sports as id (id)}
        <option value={id}>{sportOf(id).name}</option>
      {/each}
    </select>
    <div class="flex gap-1" role="group" aria-label={t('activitiesPeriod')}>
      {#each PERIODS as p (p.id)}
        <button class="btn btn-sm {period === p.id ? 'btn-primary' : 'btn-secondary'}" aria-pressed={period === p.id} onclick={() => (period = p.id)}>{p.name}</button>
      {/each}
    </div>
  </div>
  {#if error}
    <p class="px-4 pb-2 text-sm" style="color: var(--theme-error);">{error}</p>
  {/if}
  <div class="relative flex-1 min-h-[60vh]" aria-busy={loading}>
    <div bind:this={el} class="absolute inset-0" role="img" aria-label={heat ? t('activitiesHeatmapMapRoutes', { n: heat.routes.length }) : t('activitiesHeatmapMap')}></div>
    {#if empty}
      <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
        <p class="card px-4 py-3 text-sm">{t('activitiesHeatmapEmpty')}</p>
      </div>
    {/if}
  </div>
  <p class="px-4 py-2 text-xs text-text-muted">{t('activitiesHeatmapPrivate')}</p>
</div>
